import Link from 'next/link';
import React, { type HTMLAttributes, useState } from 'react';
import { toast } from '~/utils/toast';
import { Routes } from '~/config';

import { ArrowUpRight, Check, MessageCircle } from 'lucide-react';
import { type ContentMedium, PolicyFeedbackAnnotationScope, RiskLevel } from '@prisma/client';

import Button from '../Button';
import Chip from '../Chip';
import HtmlContent from '../HtmlContent';
import Quote from '../Quote';
import Tooltip from '../Tooltip';
import Suggestions from './Suggestions';
import {
  type FeedbackListExitHandlers,
  type FeedbackListProps,
  type SortedPolicyFeedback,
} from './FeedbackList';
import { type FeedbackLocation } from '~/server/utils/processors/document/processPages';
import { api, skipBatchOption } from '~/utils/api';
import IgnoreFeedback from './IgnoreFeedback';
import SimilarUsages from './SimilarUsages';
import { type SimilarUsage } from '~/server/utils/processors/policyEngine/policies/steps/helpers/findSimilarUsages';
import getUserName from '~/utils/getUserName';
import ThumbsUp, { hasTruePositiveMark, type ItemTeachingResult } from './ThumbsUp';
import { cn } from '~/lib/utils';
import AppSpinner from '~/components/Loading/AppSpinner';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import {
  FeedbackItemActions,
  FeedbackItemFrame,
  FeedbackItemHeader,
  FeedbackModeChips,
  FeedbackNewChip,
  FeedbackRiskChip,
  buildFeedbackMeta,
  formatVideoTime,
} from './FeedbackItemFrame';
import { getContentMediumLabel } from './FeedbackSource';
import { getGroupedAcknowledgementState } from './getGroupedAcknowledgementState';
import { getGroupedFeedbackIds } from './getGroupedFeedbackIds';

type AcknowledgeButtonProps = Pick<
  PolicyCardProps,
  'concatenateFeedbacks' | 'reloadDoc' | 'isAcknowledged'
>;

const AcknowledgeButton = (props: AcknowledgeButtonProps) => {
  const { data: authorized } = api.user.authorize.useQuery();
  const { mutateAsync: actionPolicyFeedback } = api.feedback.actionPolicyFeedback.useMutation();
  const [isAcknowledgedByCurrentUser, setIsAcknowledgedByCurrentUser] = useState(false);
  const [isUnacknowledged, setIsUnacknowledged] = useState(false);
  const groupedFeedbackIds = getGroupedFeedbackIds(props.concatenateFeedbacks);
  const { data: acknowledgedFeedbacks, isLoading: isGettingAcknowledgedFeedback } =
    api.feedback.getAcknowledgedFeedback.useQuery(
      {
        ids: groupedFeedbackIds,
      },
      {
        enabled: props.isAcknowledged && !isUnacknowledged && groupedFeedbackIds.length > 0,
        ...skipBatchOption,
      },
    );

  const [isLoading, setIsLoading] = useState(false);

  const setStatus = async (status: 'ACKNOWLEDGED' | 'CURRENT') => {
    await actionPolicyFeedback({
      policyFeedback: {
        ids: groupedFeedbackIds,
        status,
      },
    });
    props.reloadDoc();
  };

  if (
    !isUnacknowledged &&
    !isAcknowledgedByCurrentUser &&
    props.isAcknowledged &&
    isGettingAcknowledgedFeedback
  ) {
    return <AppSpinner size="xs" />;
  }

  const owners = (acknowledgedFeedbacks ?? []).map(
    (feedback) => feedback.PolicyFeedbackStatus[0]?.updatedByUser,
  );
  const showAcknowledged =
    !isUnacknowledged && (isAcknowledgedByCurrentUser || (acknowledgedFeedbacks?.length ?? 0) > 0);

  if (showAcknowledged) {
    const isPrivileged =
      authorized?.user.tenantRole === 'TENANT_ADMIN' ||
      authorized?.user.UserWorkspaces[0]?.Role?.isAdmin === true;
    const { canUndo, byYou, mixedOwners, otherOwner } = getGroupedAcknowledgementState({
      owners,
      currentUserId: authorized?.user.id,
      isPrivileged,
      acknowledgedByCurrentUser: isAcknowledgedByCurrentUser,
    });
    const byName = byYou || mixedOwners ? null : otherOwner ? getUserName(otherOwner) : 'Unknown';
    const chip = (
      <Chip
        level="success"
        icon={<Check strokeWidth={2.25} />}
        text={
          <span className="truncate">{byName ? `Acknowledged by ${byName}` : 'Acknowledged'}</span>
        }
        className={cn(
          'h-8 min-w-0 max-w-[12rem] overflow-hidden px-2.5',
          canUndo && 'hover:bg-emerald-200/90 cursor-pointer',
        )}
      />
    );

    if (!canUndo) {
      return chip;
    }

    return (
      <Tooltip content="Undo acknowledge" hideIcon>
        <button
          type="button"
          aria-label="Undo acknowledge"
          disabled={isLoading}
          className="rounded-md outline-none focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-50"
          onClick={async (e) => {
            e.stopPropagation();
            try {
              setIsLoading(true);
              await setStatus('CURRENT');
              setIsAcknowledgedByCurrentUser(false);
              setIsUnacknowledged(true);
            } catch (err) {
              console.error(err);
              toast.error("Couldn't undo acknowledge");
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {chip}
        </button>
      </Tooltip>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={isLoading}
      isLoading={isLoading}
      onClick={async () => {
        try {
          setIsLoading(true);
          await setStatus('ACKNOWLEDGED');
          setIsAcknowledgedByCurrentUser(true);
          setIsUnacknowledged(false);
        } catch (err) {
          console.error(err);
          toast.error("Couldn't acknowledge this finding");
        } finally {
          setIsLoading(false);
        }
      }}
    >
      Acknowledge
    </Button>
  );
};

export type PolicyCardProps = {
  children?: React.ReactNode;
  policy: {
    id: string;
    name: string;
    policyGroupId: string | null;
    hasVisionPrompt: boolean;
  } | null;
  text?: string;
  formattedOutput?: any;
  suggestions?: string[];
  explanation: string;
  reloadDoc: () => Promise<void>;
  onComment?: () => void;
  onRemove?: (feedbackId: string) => void;
  feedbackId?: string;
  onClick?: HTMLAttributes<HTMLDivElement>['onClick'];
  riskLevel: RiskLevel | null;
  isNew?: boolean;
  color: string | null | undefined;
  contentMedium: ContentMedium;
  brand: {
    id: string;
    name: string;
  } | null;
  documentId: string;
  concatenateFeedbacks: SortedPolicyFeedback[];
  isMultiPages: boolean;
  feedbackLocation: FeedbackLocation;
  isTest?: boolean;
  isResolvedFeedback: boolean;
  evaluationType: FeedbackListProps['evaluationType'];
  similarUsages: SimilarUsage[];
  isApproved: boolean;
  isAcknowledged: boolean;
  isContentScanner?: boolean;
  isPublicView?: boolean;
  isCompactView?: boolean;
  isShadowFeedback?: boolean;
  shadowModelName?: string;
  isPrimaryFeedback?: boolean;
  primaryModelName?: string;
  isInShadowMode?: boolean;
} & Partial<FeedbackListExitHandlers>;

/** Only mounted on authenticated views — public share pages have no WorkspaceProvider. */
function PolicyMoreInfoLink({
  policyGroupId,
  policyId,
  isCompactView,
}: {
  policyGroupId: string | null;
  policyId: string;
  isCompactView?: boolean;
}) {
  const { workspaceId } = useWorkspace();
  return (
    <Link
      href={`/${workspaceId}${Routes.POLICY_ENGINE}/group/${policyGroupId}/policy/${policyId}`}
      className={cn(
        'text-foreground inline-flex w-fit items-center gap-0.5 font-medium underline decoration-foreground/35 underline-offset-[3px]',
        'rounded-sm outline-none transition-colors hover:decoration-foreground',
        'focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:ring-offset-1',
        isCompactView ? 'text-xs' : 'text-sm',
      )}
      onClick={(e) => e.stopPropagation()}
    >
      More info
      <ArrowUpRight
        className={cn('shrink-0', isCompactView ? 'size-3' : 'size-3.5')}
        strokeWidth={2.25}
        aria-hidden
      />
    </Link>
  );
}

export default function PolicyCard(props: PolicyCardProps) {
  const [isResolving, setIsResolving] = useState(false);
  const [justResolved, setJustResolved] = useState(false);
  const [localResults, setLocalResults] = useState<Record<string, ItemTeachingResult>>({});
  const { mutateAsync: actionPolicyFeedback } = api.feedback.actionPolicyFeedback.useMutation();
  const groupedFeedbackIds = getGroupedFeedbackIds(props.concatenateFeedbacks, props.feedbackId);

  if (!props.policy) return <></>;

  const nonVisionFeedback = props.concatenateFeedbacks.filter(
    (i) => !i.policy.steps?.some((s) => s.visionPrompt),
  );
  const renderedFeedback = props.concatenateFeedbacks.filter(
    (obj, index, self) => self.findIndex((item) => item.explanation === obj.explanation) === index,
  );
  const actionableFeedback = renderedFeedback.filter((item) =>
    nonVisionFeedback.some((entry) => entry.id === item.id),
  );
  const isMulti = actionableFeedback.length > 1;
  const resultFor = (item: (typeof actionableFeedback)[number]): ItemTeachingResult | null => {
    const local = localResults[item.id];
    if (local) return local;
    const scope = item.PolicyFeedbackAnnotation?.[0]?.scope;
    if (!scope) return null;
    return scope === PolicyFeedbackAnnotationScope.TRUE_POSITIVE ? 'up' : 'ignore';
  };
  const settledIds = actionableFeedback.filter((item) => resultFor(item)).map((item) => item.id);
  const buttonsSettled = isMulti
    ? settledIds.length === actionableFeedback.length
    : hasTruePositiveMark(nonVisionFeedback) ||
      (!!nonVisionFeedback[0] && localResults[nonVisionFeedback[0].id] === 'up');
  const recordResult = (id: string, result: ItemTeachingResult) => {
    setLocalResults((current) => ({ ...current, [id]: result }));
  };

  const pageLabel = props.isMultiPages
    ? `Page ${Array.from(
        new Set(
          props.concatenateFeedbacks.map((i) => (i.location as FeedbackLocation).pageNumber + 1),
        ),
      )
        .sort((a, b) => a - b)
        .join(', ')}`
    : null;

  const sourceLabel = getContentMediumLabel(props.contentMedium);
  const meta = buildFeedbackMeta([
    props.feedbackLocation?.videoStartTime
      ? formatVideoTime(props.feedbackLocation.videoStartTime)
      : null,
    pageLabel,
  ]);

  return (
    <FeedbackItemFrame
      onClick={props.onClick}
      isShadowFeedback={props.isShadowFeedback}
      isPrimaryFeedback={props.isPrimaryFeedback}
      isResolvedFeedback={props.isResolvedFeedback}
      isCompactView={props.isCompactView}
      className={cn(
        justResolved ? 'opacity-70' : undefined,
        props.isRowExiting?.(props.feedbackId ?? '') && 'pointer-events-none',
      )}
    >
      <FeedbackItemHeader
        title={props.policy.name}
        color={props.color}
        badge={sourceLabel || undefined}
        meta={meta}
        isCompactView={props.isCompactView}
        trailing={
          <>
            <FeedbackModeChips
              isInShadowMode={props.isInShadowMode}
              isPrimaryFeedback={props.isPrimaryFeedback}
              isShadowFeedback={props.isShadowFeedback}
            />
            <FeedbackNewChip isNew={props.isNew} />
            {props.riskLevel && props.riskLevel !== RiskLevel.AUTO ? (
              <FeedbackRiskChip riskLevel={props.riskLevel} />
            ) : null}
          </>
        }
      />

      <div className="space-y-1.5">
        {props.text ? (
          <Quote text={props.text} className={props.isCompactView ? 'text-xs' : undefined} />
        ) : null}
        {props.formattedOutput ? (
          <pre className="bg-muted/60 overflow-x-auto rounded-md px-2.5 py-2">
            <code className="prettyprint text-[11px] leading-relaxed">
              {JSON.stringify(props.formattedOutput, null, 2)}
            </code>
          </pre>
        ) : null}

        {renderedFeedback.length <= 1 ? (
          <p
            className={cn(
              'text-foreground whitespace-pre-wrap leading-relaxed',
              props.isCompactView ? 'text-xs' : 'text-sm',
            )}
          >
            {props.explanation.trimEnd()}
            {!props.isPublicView && (
              <>
                {' '}
                <PolicyMoreInfoLink
                  policyGroupId={props.policy.policyGroupId}
                  policyId={props.policy.id}
                  isCompactView={props.isCompactView}
                />
              </>
            )}
          </p>
        ) : (
          <ol className="list-decimal space-y-2 pl-4">
            {renderedFeedback.map((item, idx) => (
              <li
                key={item.id || idx}
                className={cn(
                  'text-foreground leading-relaxed',
                  props.isCompactView ? 'text-xs' : 'text-sm',
                )}
              >
                <div className="whitespace-pre-wrap">
                  {item.text?.split(' ').length! <= 2 && item.id !== props.feedbackId && (
                    <strong>"{item.text}" — </strong>
                  )}
                  <HtmlContent html={item.explanation} />
                  {!props.isPublicView && (
                    <>
                      {' '}
                      <PolicyMoreInfoLink
                        policyGroupId={item.policy.policyGroupId}
                        policyId={item.policy.id}
                        isCompactView={props.isCompactView}
                      />
                    </>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <SimilarUsages
        similarUsages={props?.similarUsages ?? []}
        type="policy-feedback"
        isCompactView={props.isCompactView}
      />

      {props.feedbackId && !props.isPublicView && !props.isShadowFeedback && (
        <FeedbackItemActions
          leading={
            props.isResolvedFeedback ? undefined : (
              <>
                {props.text && nonVisionFeedback.length > 0 && (
                  <ThumbsUp
                    concatenateFeedbacks={nonVisionFeedback}
                    feedbackId={nonVisionFeedback[0]?.id!}
                    type={'policyFeedback'}
                    alreadyMarked={buttonsSettled}
                    settledIds={isMulti ? settledIds : undefined}
                    onMarked={(id) => recordResult(id, 'up')}
                  />
                )}
                {nonVisionFeedback.length > 0 && (
                  <IgnoreFeedback
                    feedbackId={nonVisionFeedback[0]?.id!}
                    listRowId={props.feedbackId}
                    type="policyFeedback"
                    policy={{ ...nonVisionFeedback[0]?.policy!, hasVisionPrompt: false }}
                    text={props.text}
                    reloadDoc={props.reloadDoc}
                    beginExit={props.beginExit}
                    restoreExit={props.restoreExit}
                    brand={props.brand}
                    documentId={props.documentId}
                    concatenateFeedbacks={nonVisionFeedback}
                    evaluationType={props.evaluationType}
                    disabled={buttonsSettled}
                    settledIds={isMulti ? settledIds : undefined}
                    leaveFindingOpen={isMulti}
                    onAffected={(id) => recordResult(id, 'ignore')}
                  />
                )}
                {props.onComment && !props.isTest && (
                  <Tooltip content="Comment" hideIcon>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Comment"
                      onClick={props.onComment}
                    >
                      <MessageCircle className="size-4" strokeWidth={2} />
                    </Button>
                  </Tooltip>
                )}
                {props.suggestions?.length && !props.isPublicView ? (
                  <Suggestions
                    suggestions={props.suggestions}
                    feedbackId={props.feedbackId}
                    isCompactView={props.isCompactView}
                  />
                ) : null}
              </>
            )
          }
          trailing={
            !props.isTest ? (
              props.isResolvedFeedback ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isResolving}
                  isLoading={isResolving}
                  onClick={async () => {
                    if (!props.feedbackId || !props.beginExit || !props.restoreExit) return;
                    if (isResolving || groupedFeedbackIds.length === 0) return;

                    const feedbackId = props.feedbackId;
                    const mutation = actionPolicyFeedback({
                      policyFeedback: {
                        ids: groupedFeedbackIds,
                        status: 'CURRENT',
                      },
                    });
                    props.beginExit(feedbackId, 'remove', mutation, groupedFeedbackIds);

                    try {
                      setIsResolving(true);
                      await mutation;
                    } catch (err) {
                      console.error(err);
                      props.restoreExit(feedbackId);
                      toast.error("Couldn't unresolve this finding");
                    } finally {
                      setIsResolving(false);
                    }
                  }}
                >
                  Unresolve
                </Button>
              ) : (
                <>
                  {props.isContentScanner && (
                    <AcknowledgeButton
                      reloadDoc={props.reloadDoc}
                      concatenateFeedbacks={props.concatenateFeedbacks}
                      isAcknowledged={props.isAcknowledged}
                    />
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant={justResolved ? 'outline' : 'default'}
                    disabled={isResolving || justResolved}
                    isLoading={isResolving}
                    onClick={async () => {
                      if (!props.feedbackId || !props.beginExit || !props.restoreExit) return;
                      if (isResolving || justResolved || groupedFeedbackIds.length === 0) return;

                      const feedbackId = props.feedbackId;
                      setJustResolved(true);
                      const mutation = actionPolicyFeedback({
                        policyFeedback: {
                          ids: groupedFeedbackIds,
                          status: 'SKIPPED',
                        },
                      });
                      props.beginExit(feedbackId, 'resolve', mutation, groupedFeedbackIds);

                      try {
                        setIsResolving(true);
                        await mutation;
                      } catch (err) {
                        console.error(err);
                        props.restoreExit(feedbackId);
                        setJustResolved(false);
                        toast.error('Error resolving feedback');
                      } finally {
                        setIsResolving(false);
                      }
                    }}
                  >
                    {justResolved ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Check className="size-3.5" strokeWidth={2.5} aria-hidden />
                        Resolved
                      </span>
                    ) : (
                      'Resolve'
                    )}
                  </Button>
                </>
              )
            ) : null
          }
        />
      )}
    </FeedbackItemFrame>
  );
}
